import { ref, computed, watch } from 'vue';

type ThemeSetting = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'theme';

function getStoredTheme(): ThemeSetting {
  if (typeof window === 'undefined') return 'system';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'system';
}

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

const theme = ref<ThemeSetting>(getStoredTheme());

const resolvedTheme = computed<'light' | 'dark'>(() => {
  if (theme.value === 'system') {
    return getSystemTheme();
  }
  return theme.value;
});

function applyTheme() {
  const resolved = resolvedTheme.value;
  if (resolved === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

function setTheme(value: ThemeSetting) {
  theme.value = value;
  localStorage.setItem(STORAGE_KEY, value);
  applyTheme();
}

// Apply theme immediately on module load
if (typeof window !== 'undefined') {
  applyTheme();

  // Watch for system theme changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (theme.value === 'system') {
      applyTheme();
    }
  });
}

// Watch for reactive changes
watch(theme, () => {
  applyTheme();
});

export function useTheme() {
  return {
    theme,
    resolvedTheme,
    setTheme,
  };
}
